import React, { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';

const sb = { borderLeft:'3px solid var(--gold)', padding:'.75rem 1.5rem', background:'rgba(197,165,90,0.04)' };
const sbText = { fontFamily:"'Playfair Display',serif", fontStyle:'italic', fontSize:'1.05rem', lineHeight:1.8, color:'rgba(245,240,232,0.92)', marginBottom:'.25rem' };
const sbRef = { fontFamily:"'Oswald',sans-serif", fontSize:'.8rem', letterSpacing:2, color:'var(--gold)' };

const CURATED = [
  { label:'God Manifest in the Flesh', book:54, ch:3, vs:16, note:'Modern versions change "God" to "He," removing the direct declaration of Christ\'s deity.' },
  { label:'Begotten Son (John 3:16)', book:43, ch:3, vs:16, note:'"Begotten" is removed, blurring the unique relationship between the Father and the Son.' },
  { label:'The Heavenly Witness (1 John 5:7)', book:62, ch:5, vs:7, note:'The clearest Trinitarian statement in the Bible is completely removed in modern versions.' },
  { label:'Salvation Confession (Acts 8:37)', book:44, ch:8, vs:37, note:'The Ethiopian eunuch\'s confession of faith before baptism is entirely removed.' },
  { label:'Prayer and Fasting (Matt 17:21)', book:40, ch:17, vs:21, note:'Christ\'s teaching on spiritual warfare through fasting is removed entirely.' },
  { label:'Hell vs Hades (Luke 16:23)', book:42, ch:16, vs:23, note:'"Hell" is replaced with the pagan Greek term "Hades" throughout.' },
  { label:'God is a Spirit (John 4:24)', book:43, ch:4, vs:24, note:'The article "a" is removed, changing the declaration of God\'s nature.' },
  { label:'Equal with God (Phil 2:6)', book:50, ch:2, vs:6, note:'Modern versions imply equality with God was beyond Christ\'s reach rather than already His.' },
  { label:'The Blood (Col 1:14)', book:51, ch:1, vs:14, note:'"Through his blood" is removed from the statement about redemption.' },
];

const BOOK_NAMES = {1:'Genesis',2:'Exodus',3:'Leviticus',4:'Numbers',5:'Deuteronomy',6:'Joshua',7:'Judges',8:'Ruth',9:'1 Samuel',10:'2 Samuel',11:'1 Kings',12:'2 Kings',13:'1 Chronicles',14:'2 Chronicles',15:'Ezra',16:'Nehemiah',17:'Esther',18:'Job',19:'Psalms',20:'Proverbs',21:'Ecclesiastes',22:'Song of Solomon',23:'Isaiah',24:'Jeremiah',25:'Lamentations',26:'Ezekiel',27:'Daniel',28:'Hosea',29:'Joel',30:'Amos',31:'Obadiah',32:'Jonah',33:'Micah',34:'Nahum',35:'Habakkuk',36:'Zephaniah',37:'Haggai',38:'Zechariah',39:'Malachi',40:'Matthew',41:'Mark',42:'Luke',43:'John',44:'Acts',45:'Romans',46:'1 Corinthians',47:'2 Corinthians',48:'Galatians',49:'Ephesians',50:'Philippians',51:'Colossians',52:'1 Thessalonians',53:'2 Thessalonians',54:'1 Timothy',55:'2 Timothy',56:'Titus',57:'Philemon',58:'Hebrews',59:'James',60:'1 Peter',61:'2 Peter',62:'1 John',63:'2 John',64:'3 John',65:'Jude',66:'Revelation'};

/* Strip HTML tags from API response text */
function cleanText(html) {
  if (!html) return '';
  return html
    .replace(/<\/?[^>]+(>|$)/g, '')  // strip all HTML tags
    .replace(/(?<=[a-zA-Z])\d+/g, '') // strip Strong's concordance numbers (digits glued to words)
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/\s+/g, ' ')
    .trim();
}

function VerseCompare() {
  const [book, setBook] = useState(43);
  const [chapter, setChapter] = useState(3);
  const [verse, setVerse] = useState(16);
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const fetchVerse = useCallback(async (b, c, v) => {
    const bk = b || book;
    const ch = c || chapter;
    const vs = v || verse;
    setLoading(true);
    setError('');
    setResults(null);

    const versions = ['KJV','NIV','ESV','NASB'];
    try {
      const fetches = versions.map(ver =>
        fetch(`https://bolls.life/get-verse/${ver}/${bk}/${ch}/${vs}/`)
          .then(r => {
            if (!r.ok) return { text: null, removed: true };
            return r.json();
          })
          .catch(() => ({ text: null, removed: true }))
      );
      const data = await Promise.all(fetches);

      /* Check if the KJV has the verse (it should always) */
      const kjvHasVerse = data[0].text && cleanText(data[0].text).length > 0;

      setResults(versions.map((ver, i) => {
        const raw = data[i].text ? cleanText(data[i].text) : '';
        const isEmpty = !raw || raw.length < 3;
        const isKJV = ver === 'KJV';

        if (isEmpty && !isKJV && kjvHasVerse) {
          return { version: ver, text: '', removed: true };
        }
        return { version: ver, text: raw, removed: false };
      }));
    } catch {
      setError('Unable to fetch verses. Please check your reference and try again.');
    }
    setLoading(false);
  }, [book, chapter, verse]);

  const loadCurated = (c) => {
    setBook(c.book);
    setChapter(c.ch);
    setVerse(c.vs);
    fetchVerse(c.book, c.ch, c.vs);
  };

  return (
    <div>
      {/* Selector */}
      <div style={{ display:'flex', flexWrap:'wrap', gap:'.75rem', alignItems:'flex-end', justifyContent:'center', marginBottom:'2rem' }}>
        <div>
          <label style={{ display:'block', fontFamily:"'Oswald',sans-serif", fontSize:'.65rem', letterSpacing:2, color:'var(--gold)', marginBottom:'.25rem', textTransform:'uppercase' }}>Book</label>
          <select value={book} onChange={e => setBook(+e.target.value)} style={{ padding:'.6rem 1rem', background:'rgba(255,255,255,0.06)', border:'1px solid rgba(197,165,90,0.2)', color:'var(--white)', fontFamily:"'Source Sans 3',sans-serif", fontSize:'.9rem', minWidth:160 }}>
            {Object.entries(BOOK_NAMES).map(([id,name]) => <option key={id} value={id} style={{background:'#0d1e38'}}>{name}</option>)}
          </select>
        </div>
        <div>
          <label style={{ display:'block', fontFamily:"'Oswald',sans-serif", fontSize:'.65rem', letterSpacing:2, color:'var(--gold)', marginBottom:'.25rem', textTransform:'uppercase' }}>Chapter</label>
          <input type="number" min={1} value={chapter} onChange={e => setChapter(+e.target.value)} style={{ padding:'.6rem .75rem', background:'rgba(255,255,255,0.06)', border:'1px solid rgba(197,165,90,0.2)', color:'var(--white)', fontFamily:"'Source Sans 3',sans-serif", fontSize:'.9rem', width:70 }} />
        </div>
        <div>
          <label style={{ display:'block', fontFamily:"'Oswald',sans-serif", fontSize:'.65rem', letterSpacing:2, color:'var(--gold)', marginBottom:'.25rem', textTransform:'uppercase' }}>Verse</label>
          <input type="number" min={1} value={verse} onChange={e => setVerse(+e.target.value)} style={{ padding:'.6rem .75rem', background:'rgba(255,255,255,0.06)', border:'1px solid rgba(197,165,90,0.2)', color:'var(--white)', fontFamily:"'Source Sans 3',sans-serif", fontSize:'.9rem', width:70 }} />
        </div>
        <button onClick={() => fetchVerse()} className="btn-p" style={{ padding:'.65rem 2rem', fontSize:'.8rem' }}>
          {loading ? 'Loading...' : 'Compare'}
        </button>
      </div>

      {/* Curated quick-picks */}
      <div style={{ textAlign:'center', marginBottom:'2.5rem' }}>
        <p style={{ fontFamily:"'Oswald',sans-serif", fontSize:'.7rem', letterSpacing:2, color:'rgba(245,240,232,0.4)', textTransform:'uppercase', marginBottom:'.75rem' }}>Key Verses Where Differences Matter Most</p>
        <div style={{ display:'flex', flexWrap:'wrap', gap:'.4rem', justifyContent:'center' }}>
          {CURATED.map((c,i) => (
            <button key={i} onClick={() => loadCurated(c)} style={{
              padding:'.4rem .8rem', background:'rgba(255,255,255,0.04)', border:'1px solid rgba(197,165,90,0.12)',
              color:'rgba(245,240,232,0.65)', fontFamily:"'Oswald',sans-serif", fontSize:'.65rem', letterSpacing:1,
              cursor:'pointer', transition:'.3s', textTransform:'uppercase',
            }}>{c.label}</button>
          ))}
        </div>
      </div>

      {/* Error */}
      {error && <p style={{ textAlign:'center', color:'var(--crimson)', marginBottom:'1rem' }}>{error}</p>}

      {/* Results */}
      {results && (
        <div>
          <p style={{ textAlign:'center', fontFamily:"'Oswald',sans-serif", fontSize:'.75rem', letterSpacing:2, color:'var(--gold)', marginBottom:'1.5rem', textTransform:'uppercase' }}>
            {BOOK_NAMES[book]} {chapter}:{verse}
          </p>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(280px,1fr))', gap:'1rem' }}>
            {results.map((r,i) => {
              const isKJV = r.version === 'KJV';
              return (
                <div key={i} style={{
                  padding:'1.5rem',
                  borderLeft:`3px solid ${isKJV ? 'var(--gold)' : r.removed ? 'var(--crimson)' : 'rgba(245,240,232,0.15)'}`,
                  background: isKJV ? 'rgba(197,165,90,0.06)' : r.removed ? 'rgba(178,34,52,0.06)' : 'rgba(255,255,255,0.02)',
                  border: isKJV ? '1px solid rgba(197,165,90,0.2)' : r.removed ? '1px solid rgba(178,34,52,0.15)' : '1px solid rgba(255,255,255,0.06)',
                }}>
                  <p style={{ fontFamily:"'Oswald',sans-serif", fontSize:'.7rem', letterSpacing:2, textTransform:'uppercase', color: isKJV ? 'var(--gold)' : r.removed ? 'var(--crimson)' : 'rgba(245,240,232,0.5)', marginBottom:'.5rem' }}>{r.version}</p>
                  {r.removed ? (
                    <div>
                      <p style={{ fontFamily:"'Oswald',sans-serif", fontSize:'.8rem', letterSpacing:1, textTransform:'uppercase', color:'var(--crimson)', fontWeight:700, marginBottom:'.5rem' }}>
                        ✗ Verse Removed or Footnoted
                      </p>
                      <p style={{ fontSize:'.8rem', lineHeight:1.7, color:'rgba(245,240,232,0.5)' }}>
                        This verse has been removed from the main text, placed in a footnote, or omitted entirely in this translation.
                      </p>
                    </div>
                  ) : (
                    <p style={{
                      fontFamily: isKJV ? "'Playfair Display',serif" : "'Source Sans 3',sans-serif",
                      fontStyle: isKJV ? 'italic' : 'normal',
                      fontSize: isKJV ? '1rem' : '.9rem',
                      lineHeight: 1.8,
                      color: isKJV ? 'rgba(245,240,232,0.95)' : 'rgba(245,240,232,0.7)',
                    }}>{r.text}</p>
                  )}
                </div>
              );
            })}
          </div>
          {/* Curated note */}
          {CURATED.find(c => c.book === book && c.ch === chapter && c.vs === verse)?.note && (
            <div style={{ marginTop:'1.5rem', padding:'1.25rem 1.5rem', background:'rgba(178,34,52,0.06)', border:'1px solid rgba(178,34,52,0.12)' }}>
              <p style={{ fontFamily:"'Oswald',sans-serif", fontSize:'.7rem', letterSpacing:2, color:'var(--crimson)', textTransform:'uppercase', marginBottom:'.25rem' }}>Why This Matters</p>
              <p style={{ fontSize:'.9rem', lineHeight:1.8, color:'rgba(245,240,232,0.8)' }}>
                {CURATED.find(c => c.book === book && c.ch === chapter && c.vs === verse).note}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function SideBySide() {
  return (
    <>
      {/* HERO */}
      <section className="hero" style={{ background:'linear-gradient(165deg,var(--navy) 0%,var(--deep-blue) 40%,var(--royal) 100%)', minHeight:'auto', padding:'5rem 2rem 4rem' }}>
        <div className="inner" style={{ textAlign:'center' }}>
          <div className="section-label center">See It for Yourself</div>
          <h1 className="section-title" style={{ fontSize:'clamp(1.8rem,5vw,3.2rem)', marginBottom:'1.25rem', lineHeight:1.15 }}>
            KJV vs. Modern Versions<br />Side by Side
          </h1>
          <p style={{ fontSize:'1.1rem', fontFamily:"'Playfair Display',serif", fontStyle:'italic', color:'rgba(245,240,232,0.8)', maxWidth:600, margin:'0 auto 2rem' }}>
            Pick any verse. Compare the King James Bible with the NIV, ESV, and NASB in real time.
          </p>
          <div style={{ ...sb, maxWidth:600, margin:'0 auto', textAlign:'left' }}>
            <p style={sbText}>"The words of the LORD are pure words: as silver tried in a furnace of earth, purified seven times."</p>
            <p style={sbRef}>Psalm 12:6</p>
          </div>
        </div>
      </section>

      <div className="divider" />

      {/* COMPARISON TOOL */}
      <section style={{ background:'linear-gradient(180deg,var(--navy),var(--deep-blue))' }}>
        <div className="inner">
          <VerseCompare />
        </div>
      </section>

      <div className="divider divider-gold" />

      {/* EXPLANATION */}
      <section style={{ background:'var(--deep-blue)' }}>
        <div className="inner" style={{ maxWidth:750 }}>
          <h2 className="section-title" style={{ fontSize:'clamp(1.3rem,3vw,1.8rem)' }}>Why Do the Differences Matter?</h2>
          <div className="about-content">
            <p>Some will say these are minor differences in translation style. Look closer. When "God" becomes "he," that is not style. When "hell" becomes "Hades," that is not modernization. When entire verses disappear, that is not scholarship. It is the removal of doctrine.</p>
          </div>
          <div style={{ display:'flex', gap:'1.5rem', justifyContent:'center', flexWrap:'wrap', marginTop:'2rem' }}>
            <Link to="/why-kjv" className="btn-p">Read the Full Comparison</Link>
            <Link to="/bible" className="btn-s">Read the KJV Bible</Link>
          </div>
        </div>
      </section>
    </>
  );
}
